'use client';

import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { LoaderIcon } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FC, useState } from 'react';
import * as yup from 'yup';

import { useStore } from '@/entities/store';
import { User } from '@/payload-types';
import { Button } from '@/shared/ui/atoms/button';
import { Input } from '@/shared/ui/atoms/input/ui';

type SignInProps = {
  className?: string;
};

const SignIn: FC<SignInProps> = ({ className }) => {
  const validationSchema = yup.object({
    email: yup.string().required('Required'),
    password: yup.string().required('Required'),
  });

  const router = useRouter();
  const store = useStore();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema,
    onSubmit: async (payload) => {
      try {
        setIsLoading(true);
        const { data } = await axios<{user: User}>('/api/users/login', {
          method: 'POST',
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
          data: JSON.stringify({
            email: payload.email,
            password: payload.password,
          }),
        });

        store.user.login(data?.user);

        if (data) {
          router.push('/profile');
        }
      } catch {
        formik.setErrors({ password: 'Неверный логин или пароль' });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <form
      className={classNames(
        'mt-36 mx-auto flex flex-col gap-6 max-w-96 w-full bg-gray-900 backdrop-blur-sm p-4 rounded-lg',
        className,
      )}
      onSubmit={formik.handleSubmit}
    >
      <h2 className="text-red-600 text-center font-bold text-2xl">
        Авторизация
      </h2>
      <Input
        id="email"
        placeholder="Почта или никнейм"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email ? formik.errors.email : ''}
      />
      <Input
        id="password"
        type="password"
        placeholder="Пароль"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
        error={formik.touched.password ? formik.errors.password : ''}
      />

      <Button type="submit" disabled={isLoading}>
        {isLoading ? <LoaderIcon className="animate-spin" /> : 'Войти'}
      </Button>
      <Link
        className="hover:underline hover:text-red-800 text-sm text-right"
        href="/auth/forgot-password"
      >
        Забыл пароль
      </Link>
    </form>
  );
};

export { SignIn };
